import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Layout classes shared by the inline editor and an empty field's placeholder,
 *  so a field takes the same space whether or not it is armed. The negative
 *  margin cancels the padding: wrapping text in this span never moves a single
 *  glyph or changes where a line breaks. An empty field shows its placeholder
 *  (data-ph) through ::before. */
const FIELD = '-mx-0.5 rounded-[3px] px-0.5 empty:before:opacity-50 empty:before:content-[attr(data-ph)]';

/** The field states on the canvas (Flowmark's): a soft blue ring on hover, a
 *  solid blue ring with a faint blue fill while typing. Rings are box-shadows,
 *  so neither state moves anything. The 3px corners are on this span, never on
 *  the button around a button label: a CTA keeps its own shape. */
const STATES = 'cursor-text outline-none transition-[box-shadow,background-color] duration-[120ms] ease-out '
  + 'hover:[box-shadow:0_0_0_2px_rgba(0,113,227,0.22)] '
  + 'focus:[box-shadow:0_0_0_2px_#0071e3] focus:bg-[rgba(0,113,227,0.06)]';

/** An empty field's placeholder on the editor canvas, for a section that is not
 *  armed for editing (a canvas without onEditText): exactly what EditableText
 *  renders for an empty value, minus the editing. Never rendered on public pages. */
export function EmptyField({ placeholder }: { placeholder: string }) {
  return <span data-ph={placeholder} className={FIELD} />;
}

/** Clipboard text the way a field takes it: plain, with runs of whitespace
 *  collapsed to one space. A multi-line field keeps its line breaks and only
 *  collapses the spaces and tabs within a line. Pure. */
export function cleanPaste(raw: string, multiline?: boolean): string {
  if (!multiline) return raw.replace(/\s+/g, ' ');
  return raw.replace(/\r\n?/g, '\n').replace(/[^\S\n]+/g, ' ');
}

/** ⌘S / Ctrl+S (not ⇧, not ⌥): the editor's save. */
function isSaveKey(e: React.KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key.toLowerCase() === 's' || e.code === 'KeyS');
}

/** Insert text at the caret. execCommand keeps the browser's own undo stack
 *  (⌘Z undoes the paste); where it is missing, a plain range insert. */
function insertPlain(el: HTMLElement, text: string) {
  const doc = el.ownerDocument;
  try {
    if (typeof doc.execCommand === 'function' && doc.execCommand('insertText', false, text)) return;
  } catch { /* fall through to the manual insert */ }
  const sel = doc.defaultView?.getSelection?.();
  if (!sel || !sel.rangeCount || !el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
    el.append(text);
    return;
  }
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const node = doc.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Put the caret at a viewport point (where the user clicked), or at the end. */
function placeCaret(el: HTMLElement, at: { x: number; y: number } | null) {
  const doc = el.ownerDocument;
  const sel = doc.defaultView?.getSelection?.();
  if (!sel) return;
  let range: Range | null = null;
  if (at) {
    const d = doc as Document & {
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
    };
    if (typeof d.caretPositionFromPoint === 'function') {
      const p = d.caretPositionFromPoint(at.x, at.y);
      if (p && el.contains(p.offsetNode)) {
        range = doc.createRange();
        range.setStart(p.offsetNode, p.offset);
        range.collapse(true);
      }
    } else if (typeof d.caretRangeFromPoint === 'function') {
      const r = d.caretRangeFromPoint(at.x, at.y);
      if (r && el.contains(r.startContainer)) range = r;
    }
  }
  if (!range) {
    range = doc.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Should a field edit as a block? Only when it is the one thing inside a
 *  block-level parent (a headline, a paragraph, a list item's text): then a
 *  block child wraps every line exactly like the text did, and the hover and
 *  focus rings frame the whole text instead of one box per line. Never inside
 *  an inline parent, next to other text, or in a box that clips with an
 *  ellipsis or a line clamp. */
function editsAsBlock(el: HTMLElement): boolean {
  const p = el.parentElement;
  const view = el.ownerDocument.defaultView;
  if (!p || !view || p.childNodes.length !== 1) return false;
  const cs = view.getComputedStyle(p);
  const d = cs.display || 'inline';
  return d !== 'inline' && d !== 'contents' && d !== 'none' && !d.includes('-webkit-box') && cs.textOverflow !== 'ellipsis';
}

/** Inline text editor for the Visual canvas. PageView arms one on every text
 *  field of every section in edit mode, so a single click on any text starts
 *  typing right there.
 *
 *  Two states, so arming a field never changes how it looks:
 *  - Display (not focused): renders `display`, the exact output the public page
 *    renders for this field (e.g. the headline with its highlighted words).
 *  - Editing (focused): the span turns into a plain-text contentEditable that
 *    React does not manage (uncontrolled on purpose: re-renders never clobber an
 *    in-progress edit). A click enters it with the caret where you clicked; Tab
 *    focus enters it with the caret at the end.
 *  Blur commits and restores the display. Enter (Shift+Enter too) commits a
 *  single-line field; a multi-line one keeps its newlines. Escape reverts and
 *  blurs without committing. A paste is plain text with its whitespace
 *  collapsed. ⌘S / Ctrl+S commits and reaches the editor's save shortcut.
 *
 *  NEVER rendered on public pages: the non-editable render path returns the raw
 *  string (or the highlighted words), byte-identical to the pre-feature output. */
export default function EditableText({ value, onCommit, multiline, placeholder, display }: {
  value: string;
  onCommit: (text: string) => void;
  multiline?: boolean;
  /** Shown via CSS when the field is empty. Without it an empty field collapses
   *  to nothing — a blank CTA button reads as a broken render, not as
   *  "click here to name this button". Never part of the committed value. */
  placeholder?: string;
  /** What the field shows while it is not being edited: the same output the
   *  public page renders. Absent = the plain value. Editing always works on the
   *  plain text. */
  display?: ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  const caretAt = useRef<{ x: number; y: number } | null>(null);
  const latest = useRef({ value, onCommit });
  latest.current = { value, onCommit };
  // The parent the block check last ran against (its node, classes and child
  // count): the check only re-runs when one of them changes.
  const blockFor = useRef<{ parent: Element | null; cls: string; n: number } | null>(null);

  // innerText preserves the line breaks plaintext-only editing inserts (<br>);
  // jsdom lacks it, so fall back to textContent. NBSPs (from caret-end typing)
  // are normalized back to plain spaces; a trailing <br> reads as a stray '\n'.
  // innerText also applies CSS text-transform, so an eyebrow styled `uppercase`
  // would be saved in capitals: switch the transform off for the read.
  const read = (el: HTMLElement) => {
    const before = el.style.textTransform;
    el.style.textTransform = 'none';
    const text = el.innerText ?? el.textContent ?? '';
    el.style.textTransform = before;
    return text.replace(/\u00a0/g, ' ').replace(/\n$/, '');
  };

  // Entering edit mode: React has just removed the display children, so the
  // span is empty and ours. Fill it with the plain text, focus it and place the
  // caret, all before the browser paints.
  useLayoutEffect(() => {
    editingRef.current = editing;
    if (!editing) return;
    const el = ref.current;
    if (!el) return;
    el.textContent = latest.current.value;
    if (el.ownerDocument.activeElement !== el) el.focus({ preventScroll: true });
    placeCaret(el, caretAt.current);
    caretAt.current = null;
  }, [editing]);

  // Block or inline (see editsAsBlock). Set on the node directly: React never
  // manages this span's style.
  useLayoutEffect(() => {
    const el = ref.current;
    const p = el?.parentElement ?? null;
    if (!el) return;
    const key = { parent: p, cls: p?.className ?? '', n: p?.childNodes.length ?? 0 };
    const last = blockFor.current;
    if (last && last.parent === key.parent && last.cls === key.cls && last.n === key.n) return;
    blockFor.current = key;
    el.style.display = editsAsBlock(el) ? 'block' : '';
  });

  // Unmount safety net (step switch / selection change while focused): blur
  // normally commits first, but if the node is torn down mid-edit, commit here.
  useEffect(() => {
    const el = ref.current;
    return () => {
      if (el && editingRef.current) {
        editingRef.current = false;
        const text = read(el);
        if (text !== latest.current.value) latest.current.onCommit(text);
      }
    };
  }, []);

  return (
    <span
      ref={ref}
      data-edit-text
      data-ph={placeholder || undefined}
      contentEditable={editing ? 'plaintext-only' : undefined}
      tabIndex={0}
      role="textbox"
      aria-multiline={multiline || undefined}
      aria-placeholder={placeholder || undefined}
      spellCheck={editing}
      className={`${FIELD} ${STATES}`}
      onMouseDown={(e) => {
        // Display state: the browser can't put a caret in it, so swap to the
        // editor first and place the caret at the click ourselves.
        if (editing || e.button !== 0) return;
        e.preventDefault();
        caretAt.current = { x: e.clientX, y: e.clientY };
        setEditing(true);
      }}
      onFocus={() => { if (!editing) setEditing(true); }}
      onBlur={(e) => {
        const el = e.currentTarget;
        const text = read(el);
        if (editingRef.current) {
          editingRef.current = false;
          el.textContent = ''; // hand the (unmanaged) span back to React for the display
          setEditing(false);
        }
        if (text !== value) onCommit(text);
      }}
      onKeyDown={(e) => {
        // ⌘S / Ctrl+S: commit (blur), and let the key go on to the editor's
        // save shortcut instead of keeping it in here.
        if (isSaveKey(e)) {
          if (editingRef.current) e.currentTarget.blur();
          return;
        }
        // Keep canvas/app-level shortcuts (Esc deselect, hotkeys) out of typing.
        e.stopPropagation();
        if (!editing) return;
        if (e.key === 'Enter' && !multiline) {
          e.preventDefault();
          e.currentTarget.blur(); // blur commits
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.currentTarget.textContent = value; // revert…
          e.currentTarget.blur();              // …then blur compares equal → no commit
        }
      }}
      onPaste={(e) => {
        if (!editingRef.current) return;
        e.preventDefault();
        insertPlain(e.currentTarget, cleanPaste(e.clipboardData?.getData('text/plain') ?? '', multiline));
      }}
      // Suppress the click's default/bubble so editing inside an <a> (CTA
      // label) or <summary> (FAQ question) never navigates or toggles, and the
      // section-select capture logic stays clean.
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >{editing || !value ? null : (display ?? value)}</span>
  );
}
